import React, { useCallback, useEffect } from "react";
import { Pressable, View } from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import { kycStyles as styles } from "./styles";
import { useFlowNavigation } from "./useFlowNavigation";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RouteProp } from "@react-navigation/native";
import type { OnboardingStackParamList, KYCStatusType } from "./types";

interface KYCStatusProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "KYCStatus">;
  route: RouteProp<OnboardingStackParamList, "KYCStatus">;
}

type StatusVariant = "success" | "failure" | "info";

interface StatusConfig {
  title: string;
  subtitle: string;
  showRetry: boolean;
  retryLabel: string;
  /** autoAdvance=true → auto-navigate after 2s; false → user must act */
  autoAdvance: boolean;
  /** special=true → navigate outside step sequence (e.g. reactivated → NearestHub) */
  specialTarget?: keyof OnboardingStackParamList;
  variant: StatusVariant;
  icon: string;
}

const STATUS_CONFIG: Record<KYCStatusType, StatusConfig> = {
  kyc_completed: {
    title: "KYC Completed",
    subtitle: "Congratulations! Your KYC has been completed.",
    showRetry: false,
    retryLabel: "",
    autoAdvance: true,
    variant: "success",
    icon: "✅",
  },
  kyc_details_fetched: {
    title: "KYC Details fetched",
    subtitle: "Congratulations! Your KYC details have been fetched.",
    showRetry: false,
    retryLabel: "",
    autoAdvance: true,
    variant: "success",
    icon: "✅",
  },
  request_accepted: {
    title: "Request Accepted",
    subtitle: "Congratulations! Your request has been accepted.",
    showRetry: false,
    retryLabel: "",
    autoAdvance: true,
    variant: "success",
    icon: "✅",
  },
  request_sent: {
    title: "Request Sent",
    subtitle: "Your request has been submitted. We will get back to you soon.",
    showRetry: false,
    retryLabel: "",
    autoAdvance: false,
    variant: "info",
    icon: "📨",
  },
  kyc_not_completed: {
    title: "KYC not completed",
    subtitle: "Please try again",
    showRetry: true,
    retryLabel: "Retry",
    autoAdvance: false,
    variant: "failure",
    icon: "❌",
  },
  request_failed: {
    title: "Request Failed",
    subtitle: "Please try again",
    showRetry: true,
    retryLabel: "Retry",
    autoAdvance: false,
    variant: "failure",
    icon: "❌",
  },
  complete_kyc: {
    title: "Complete KYC",
    subtitle: "Login krne k liye apna KYC complete karen!",
    showRetry: true,
    retryLabel: "Complete KYC",
    autoAdvance: false,
    variant: "info",
    icon: "📋",
  },
  reactivated: {
    title: "Account Reactivated",
    subtitle: "Your account has been reactivated. Please select your hub.",
    showRetry: false,
    retryLabel: "",
    autoAdvance: true,
    specialTarget: "NearestHub",
    variant: "success",
    icon: "✅",
  },
};

const KYCStatus: React.FC<KYCStatusProps> = ({ navigation, route }) => {
  const { status } = route.params;
  const config = STATUS_CONFIG[status];
  const { goToNextStep } = useFlowNavigation();

  const advance = useCallback(() => {
    if (config.specialTarget) {
      navigation.navigate(config.specialTarget as never);
    } else {
      goToNextStep();
    }
  }, [config.specialTarget, goToNextStep, navigation]);

  const onRetry = useCallback(() => {
    navigation.navigate("AadhaarVerification");
  }, [navigation]);

  useEffect(() => {
    if (config.autoAdvance) {
      const timer = setTimeout(advance, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [config.autoAdvance, advance]);

  const iconWrapperStyle = [
    styles.iconWrapper,
    config.variant === "success" && styles.iconWrapperSuccess,
    config.variant === "failure" && styles.iconWrapperFailure,
    config.variant === "info" && styles.iconWrapperInfo,
  ];

  const titleStyle = [
    styles.statusTitle,
    config.variant === "success" && styles.statusTitleSuccess,
    config.variant === "failure" && styles.statusTitleFailure,
    config.variant === "info" && styles.statusTitleInfo,
  ];

  return (
    <>
      <View style={iconWrapperStyle}>
        <Text style={styles.iconEmoji}>{config.icon}</Text>
      </View>

      <Text style={titleStyle}>{config.title}</Text>

      {config.subtitle ? (
        <Text variant="body_text_1" style={styles.statusSubtitle}>
          {config.subtitle}
        </Text>
      ) : null}

      {config.showRetry && (
        <Pressable
          style={styles.retryButton}
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={config.retryLabel}
        >
          <Text variant="title_16" style={styles.retryButtonText}>
            {config.retryLabel}
          </Text>
        </Pressable>
      )}
    </>
  );
};

export default KYCStatus;
